import { doc, getDoc, updateDoc } from 'firebase/firestore';

import { db } from '~/config/firebase.config';
import { cleanVenueName } from '~/utils/playerConsent';

/**
 * Recopie le nom de l'établissement dans `clients/{id}/public/userData.venueName`,
 * pour que la manette puisse l'afficher dans la case newsletter.
 *
 * Pourquoi : la manette (joueur non connecté) n'a pas le droit de lire
 * `clients/{id}` — seul le sous-dossier `public` lui est ouvert, et c'est déjà là
 * qu'elle lit la liste des champs à demander. L'écran, lui, a le client en main.
 *
 * Garde-fous :
 * - n'écrit que si le document existe déjà (établissement qui a ouvert la page
 *   « données joueurs » du dashboard) — on ne crée rien chez les autres ;
 * - n'écrit que si le nom a changé (une écriture par établissement, pas par écran) ;
 * - ne jette jamais : un échec laisse la manette sur la formulation neutre.
 * Le document ne contient que des booléens pour la config ; la manette et le
 * dashboard ignorent les champs non booléens, donc ce champ texte ne les gêne pas.
 */
export type VenueNameStore = {
    read: (clientID: string) => Promise<Record<string, unknown> | undefined>;
    write: (clientID: string, venueName: string) => Promise<void>;
};

const firestoreStore: VenueNameStore = {
    read: async (clientID) => {
        const snap = await getDoc(doc(db, 'clients', clientID, 'public', 'userData'));
        return snap.exists() ? snap.data() : undefined;
    },
    write: (clientID, venueName) => updateDoc(doc(db, 'clients', clientID, 'public', 'userData'), { venueName }),
};

/** @returns true si une écriture a eu lieu (utile aux tests). */
export async function syncPublicVenueName(
    clientID: string | undefined,
    rawName: unknown,
    store: VenueNameStore = firestoreStore
): Promise<boolean> {
    const venueName = cleanVenueName(rawName);
    if (!clientID || !venueName) return false;
    try {
        const current = await store.read(clientID);
        if (!current || current.venueName === venueName) return false;
        await store.write(clientID, venueName);
        return true;
    } catch (error) {
        console.warn('[syncPublicVenueName] skipped:', error);
        return false;
    }
}
